import React, { useState, useEffect, ReactElement, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, FormikHelpers } from 'formik';
import { RotateLoader } from 'react-spinners';

import { useNotify } from '../Common/snackbarHooks';
import { CancelLink } from '../Common/ButtonLinks';
import { commonStyles, css } from '../Common/styling';
import { NotificationContextValue, OptionsInterface, RoleOptionsInterface } from '../Common/types';
import NotificationContext from '../Common/NotificationContext';

import JobOpeningForm from './components/JobOpeningForm';
import { validationSchema, initialJobOpeningValues } from './jobOpeningSchema';

import { getRolesMinimalList } from '../Common/commonApi';
import { createJobOpening } from './jobOpeningApi';
import { getResidencesMinimalList } from '../Residence/residenceApi';
import { getResidenceClustersMinimalList } from '../ResidenceCluster/residenceClusterApi';
import { getResidenceGroupsMinimalList } from '../ResidenceGroup/residenceGroupApi';
import { getRegionsMinimalList } from '../Region/regionApi';
import { getDefaultsWithActualValues } from '../Common/utilities';

const CreateJobOpening: React.FC = () => {
  // hooks
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useNotify();
  const [residenceClustersMinimalList, setResidenceClustersMinimalList] = useState([]);
  const [residenceGroupsMinimalList, setResidenceGroupsMinimalList] = useState([]);
  const [residenceMinimalList, setResidenceMinimalList] = useState<OptionsInterface[]>([]);
  const [regionsMinimalList, setRegionsMinimalList] = useState([]);
  const [roleMinimalList, setRoleMinimalList] = useState<RoleOptionsInterface[]>([]);
  const [residenceClusters, setResidenceClusters] = useState<Array<OptionsInterface>>([]);
  const [residenceGroups, setResidenceGroups] = useState<Array<OptionsInterface>>([]);
  const [regions, setRegions] = useState<Array<OptionsInterface>>([]);
  const [loading, setLoading] = useState(true);
  const [loadingResidenceMinimalList, setLoadingResidenceMinimalList] = useState(true);
  const [loadingRolesMinimalList, setLoadingRolesMinimalList] = useState(true);
  const { refreshJobOpeningsNotification } = useContext(NotificationContext) as NotificationContextValue;

  // functions
  useEffect(() => {
    setLoading(true);
    setLoadingResidenceMinimalList(true);
    setLoadingRolesMinimalList(true);
    getResidenceClustersMinimalList()
      .then(({ data }) => setResidenceClustersMinimalList(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över grupper'));

    getResidenceGroupsMinimalList({ is_active: true })
      .then(({ data }) => setResidenceGroupsMinimalList(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över avtalspart'));

    getRegionsMinimalList({ is_active: true })
      .then(({ data }) => setRegionsMinimalList(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över regioner'));

    getResidencesMinimalList({ is_active: true })
      .then(({ data }) => setResidenceMinimalList(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över boenden'))
      .finally(() => setLoadingResidenceMinimalList(false));

    getRolesMinimalList({ hide_banned: true })
      .then(({ data }) => {
        setRoleMinimalList(data.data);
      })
      .catch(() => notifyError('Det gick inte att hämta listan över roller'))
      .finally(() => setLoadingRolesMinimalList(false));

    setLoading(false);
  }, []); // eslint-disable-line

  const handleFormSubmit = (
    { roles, residences, ...values }: typeof initialJobOpeningValues,
    actions: FormikHelpers<any>
  ): void => {
    setLoading(true);

    const teamLeaderRole = roleMinimalList.find((role) => role.name == 'team_leader');
    const role_ids = [...roles.map(({ id }) => id)];
    if (!role_ids.length && roleMinimalList.length > 0 && teamLeaderRole) {
      role_ids.push(teamLeaderRole.id);
    }

    // TODO: FIX THIS
    // @ts-ignore
    createJobOpening({ ...values, role_ids, residence_ids: residences.map(({ id }) => id) })
      .then(() => setLoading(false))
      .then(() => notifySuccess('Jobannons har skapats'))
      .then(() => refreshJobOpeningsNotification())
      .then(() => navigate(`/job_openings`))
      .catch((error: any) => {
        setLoading(false);
        notifyError(`Det gick inte att skapa jobannonsen: \n${error}`);
        actions.setSubmitting(false);
      });
  };

  // render
  return (
    <React.Fragment>
      <div className={css(commonStyles.createViewHeader)}>
        <h1 className={css(commonStyles.headerTextStyle)}>Lägg till jobbannons</h1>
        <CancelLink link={`/job_openings`} />
      </div>
      {(loading || loadingResidenceMinimalList || loadingRolesMinimalList) && (
        <div className={css(commonStyles.spinner)}>
          <RotateLoader loading={loading || loadingResidenceMinimalList || loadingRolesMinimalList} />
        </div>
      )}
      {!loading && !loadingResidenceMinimalList && !loadingRolesMinimalList && (
        <div>
          <div className={css(commonStyles.formContainer)}>
            <Formik
              initialValues={getDefaultsWithActualValues(initialJobOpeningValues, {
                roles: roleMinimalList.filter((role) => role.short_name === 'TL'),
              })}
              validationSchema={validationSchema}
              onSubmit={(values, actions): void => {
                handleFormSubmit(values, actions);
              }}
            >
              {({
                values,
                errors,
                handleChange,
                handleSubmit,
                setFieldValue,
                isSubmitting,
                isValid,
                dirty,
              }): ReactElement => (
                <form onSubmit={handleSubmit}>
                  <JobOpeningForm
                    values={values}
                    handleChange={handleChange}
                    errors={errors}
                    roleMinimalList={roleMinimalList}
                    residenceMinimalList={residenceMinimalList}
                    residenceClustersMinimalList={residenceClustersMinimalList}
                    residenceGroupsMinimalList={residenceGroupsMinimalList}
                    regionsMinimalList={regionsMinimalList}
                    setResidenceClusters={setResidenceClusters}
                    setResidenceGroups={setResidenceGroups}
                    setRegions={setRegions}
                    residenceClusters={residenceClusters}
                    residenceGroups={residenceGroups}
                    regions={regions}
                    isCreate={true}
                    setFieldValue={setFieldValue}
                    submitDisabled={isSubmitting || !isValid || !dirty}
                  />
                </form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default CreateJobOpening;
